/**
 * Native-OS notifications.
 *
 * Two delivery paths picked at runtime:
 *
 *   1. Electron desktop (window.location.protocol === 'file:') — call
 *      window.electronAPI.notifications.show() which routes through
 *      the main process and uses Electron's Notification class. This
 *      is reliable under file:// where the Web Notification API
 *      sometimes silently returns 'denied'. Click handler is
 *      registered once via .onClick — main forwards click → route.
 *
 *   2. Browser — Web Notification API directly. Permission is asked
 *      lazily on first attempt; subsequent calls reuse the answer.
 *
 *   showMessageNotification({ title, body, dmChannelId })
 *   showCallNotification({ callerName, dmChannelId, ... })
 *
 * Both helpers no-op when:
 *   - the API is unavailable (very old browsers, no Electron bridge),
 *   - the document is currently focused on that DM (the user already
 *     sees the message via the chat scroll),
 *   - the user has denied notifications at the OS level.
 */

// Resolve the icon relative to the bundle so it works under both
// http(s):// (browser) and file:// (Electron with vite --base=./).
// `import.meta.env.BASE_URL` is `./` for the Electron build and `/`
// for the web build — appending the asset name lands in either case.
const ICON_URL = (() => {
  if (typeof window === 'undefined') return ''
  const base = (import.meta as any).env?.BASE_URL ?? '/'
  return `${base}logo-icon.svg`.replace(/\/+/g, '/')
})()

function electronApi(): any | null {
  return typeof window !== 'undefined' ? (window as any).electronAPI ?? null : null
}

// Detect the Electron shell. OSPROD-270: the Lite desktop app loads the
// LIVE site (https://os.rx2go.ai), so the old `protocol === 'file:'`
// check missed it and macOS users fell into the renderer Web
// Notification path — which silently no-ops when Notification Center
// doesn't trust the shell (unsigned/dev builds) and has no fallback.
// Prefer the preload bridge whenever it exists: the main-process
// Notification class is reliable, and main pops its own always-on-top
// fallback window when the OS swallows the toast. Keep the file: check
// for older preloads without the notifications bridge.
function inElectron(): boolean {
  if (typeof window === 'undefined') return false
  if (electronApi()?.notifications?.show) return true
  return window.location.protocol === 'file:'
}

// Module-level navigate hook. App.tsx (after BrowserRouter mounts)
// registers the react-router `navigate` function via setGlobalNavigate.
// We use that for notification clicks because pushState+popstate can be
// flaky across React 18 batching + react-router internals — the hook
// gives us a deterministic round-trip through the router itself.
type NavigateFn = (to: string) => void
let _globalNavigate: NavigateFn | null = null
export function setGlobalNavigate(fn: NavigateFn): void {
  _globalNavigate = fn
}
function navigateToRoute(route: string): void {
  if (_globalNavigate) {
    try {
      _globalNavigate(route)
      return
    } catch { /* fall through */ }
  }
  // Fallback path — used during the brief window between page load
  // and App.tsx mounting. Manual popstate is enough here because the
  // router will mount + read the URL on next paint.
  try {
    window.history.pushState({}, '', route)
    window.dispatchEvent(new PopStateEvent('popstate'))
  } catch { /* noop */ }
}

// Click bridge — the main process emits 'notification:click' with the
// route the user wanted to open. Installed eagerly at module load (not
// lazily on first show) so a notification fired by another path still
// navigates correctly the first time.
let clickHookInstalled = false
function installElectronClickHook() {
  if (clickHookInstalled || !inElectron()) return
  const api = electronApi()
  if (!api?.notifications?.onClick) return
  clickHookInstalled = true
  api.notifications.onClick((data: { route?: string }) => {
    if (!data?.route) return
    navigateToRoute(data.route)
  })
}
// Eager install — fires once per page load. Safe under HMR because the
// `clickHookInstalled` flag is module-scoped.
if (typeof window !== 'undefined') {
  installElectronClickHook()
}

let permissionPromise: Promise<NotificationPermission> | null = null

/** Ask once on first attempt; subsequent calls reuse the answer. */
function ensurePermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return Promise.resolve('denied')
  if (Notification.permission === 'granted') return Promise.resolve('granted')
  if (Notification.permission === 'denied') return Promise.resolve('denied')
  if (!permissionPromise) {
    try {
      permissionPromise = Notification.requestPermission()
    } catch {
      permissionPromise = Promise.resolve('denied')
    }
  }
  return permissionPromise
}

interface MessageNotifyOpts {
  title: string
  body: string
  /**
   * Either a DM id (`dmChannelId`) or a server channel id. Backwards-
   * compat: if `dmChannelId` is set we route to /channels/dm-<id>,
   * otherwise `channelId` routes to /channels/<id>.
   */
  dmChannelId?: string
  channelId?: string
  /** Optional — when present, the click route appends ?msg=<id> so the
   *  destination chat can scroll-to-and-highlight that message. */
  messageId?: string
  /** Tag override — defaults to a per-conversation tag so repeats from
   *  the same chat collapse on the OS side. Override for "mention"
   *  notifications so they stack independently. */
  tag?: string
  /** When true, skip the "page-is-focused-on-this-channel" suppression
   *  (e.g. mentions — the user wants to know even if they're already on
   *  the channel but scrolled away / minimized). */
  forceShow?: boolean
}

function routeForMessage(opts: { dmChannelId?: string; channelId?: string; messageId?: string }): string | null {
  let base: string | null = null
  if (opts.dmChannelId) base = `/channels/dm-${opts.dmChannelId}`
  else if (opts.channelId) base = `/channels/${opts.channelId}`
  if (!base) return null
  return opts.messageId ? `${base}?msg=${encodeURIComponent(opts.messageId)}` : base
}

/**
 * Show a system notification for a new chat message (DM or channel).
 * Resolves the click by focusing the window + navigating to the chat
 * via pushState. When `messageId` is present the URL carries `?msg=<id>`
 * so the message list can scroll-to-and-highlight on mount.
 */
export async function showMessageNotification(opts: MessageNotifyOpts) {
  if (typeof window === 'undefined') return
  const route = routeForMessage(opts)
  if (!route) return
  const baseRoute = route.split('?')[0]
  const path = window.location.pathname

  // If the page is in the foreground AND on this chat, the user
  // already sees the message inline — no native popup needed (unless
  // forceShow is set, e.g. for @-mentions).
  const onThisChat = path === baseRoute
  if (!opts.forceShow && document.hasFocus() && onThisChat) return

  const tag = opts.tag ?? `chat-${opts.dmChannelId ?? opts.channelId}`

  if (inElectron()) {
    installElectronClickHook()
    const api = electronApi()
    if (!api?.notifications?.show) {
      console.warn('[notifications] electronAPI.notifications.show missing — preload not bridged?')
      return
    }
    try {
      const r = await api.notifications.show({
        title: opts.title,
        body: opts.body,
        tag,
        route,
        silent: false,
      })
      console.log('[notifications] electron show result', r)
    } catch (e) {
      console.error('[notifications] electron show threw', e)
    }
    return
  }

  if (typeof Notification === 'undefined') return
  const permission = await ensurePermission()
  if (permission !== 'granted') return

  try {
    const n = new Notification(opts.title, {
      body: opts.body,
      icon: ICON_URL,
      tag, // collapse repeated notifs from same chat (or stand-alone for mentions)
      silent: false,
    })
    n.onclick = () => {
      try { window.focus() } catch { /* noop */ }
      navigateToRoute(route)
      n.close()
    }
  } catch { /* noop */ }
}

interface CallNotifyOpts {
  callerName: string
  callType: 'audio' | 'video'
  dmChannelId: string
}

/**
 * Show a system notification for an incoming call. Persists with
 * `requireInteraction: true` so it stays visible until the user clicks
 * (matches Telegram/Discord OS-level call ringing).
 */
export async function showCallNotification(opts: CallNotifyOpts) {
  if (typeof window === 'undefined') return

  const title = `${opts.callerName} is calling`
  const body = opts.callType === 'video' ? 'Incoming video call' : 'Incoming audio call'
  const route = `/channels/dm-${opts.dmChannelId}`

  if (inElectron()) {
    installElectronClickHook()
    const api = electronApi()
    if (!api?.notifications?.show) return
    try {
      await api.notifications.show({
        title,
        body,
        tag: `call-${opts.dmChannelId}`,
        route,
        requireInteraction: true,
        silent: false,
      })
    } catch { /* noop */ }
    return
  }

  if (typeof Notification === 'undefined') return
  const permission = await ensurePermission()
  if (permission !== 'granted') return

  try {
    const n = new Notification(title, {
      body,
      icon: ICON_URL,
      tag: `call-${opts.dmChannelId}`,
      // Stay until acted on. Some browsers ignore this and auto-dismiss
      // after their own timer; the in-app toast acts as a fallback.
      requireInteraction: true,
      silent: false,
    })
    n.onclick = () => {
      try {
        window.focus()
        if (window.location.pathname !== route) {
          window.history.pushState({}, '', route)
          window.dispatchEvent(new PopStateEvent('popstate'))
        }
      } catch { /* noop */ }
      n.close()
    }
  } catch { /* noop */ }
}

/** Dismiss any active call notification (e.g., when the call ends). */
export function dismissCallNotification(_dmChannelId: string) {
  // The `tag` collapse is the simplest cleanup path: a fresh notification
  // with the same tag replaces the previous one. We send an empty body
  // and immediately close — the OS removes the original from the tray.
  if (typeof Notification === 'undefined') return
  try {
    const n = new Notification('', { tag: `call-${_dmChannelId}`, body: '', silent: true })
    setTimeout(() => n.close(), 0)
  } catch { /* noop */ }
}

/** Pre-warm permission. Call once on app startup if you want the
 *  prompt to appear during onboarding rather than on first message. */
export function requestNotificationPermission(): Promise<NotificationPermission> {
  return ensurePermission()
}
